import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ContractAPIService } from 'src/app/extensions/services/api/contracts-api.service';
import { Subscription } from 'rxjs';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { DEFAULT_DEBOUNCE_CHARACTERS, TABLE_VIEW_MODES } from 'src/app/extensions/constants/commonConstants';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { debounce } from 'src/app/extensions/utilities';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { isNotEmpty, scrollTop, setTableViewMode } from 'src/app/extensions/utilities';
import { DecimalPipe } from '@angular/common';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContractListFilterComponent } from 'src/app/features/my-contracts/contract-list/contract-list-filter/contract-list-filter.component';

@Component({
  selector: 'admin-contract-list',
  templateUrl: './contract-list.component.html',
  styleUrls: ['./contract-list.component.scss'],
  providers: [ScrollService],
  standalone: false,
})
export class AdminContractListComponent implements OnInit, OnDestroy {
  @ViewChild('contractSearch') public contractSearchControl: TextBoxComponent;

  public contracts: app.models.ContractModel[] = [];
  public contractsProps: app.models.DynamicPropsDictionary;

  public activeFilterCount = 0;
  public hasData = false;

  public contractsFilter: app.models.ContractsFilterModel;
  private initialFilterState: app.models.ContractsFilterModel;

  public autocompleteDatasource: app.models.Partner[] = [];
  public autocompleteValue: any = { id: undefined, value: '' };
  public partnerSearchValue: string;
  public hasPartners: boolean = true;
  public isCompanySelected: boolean = false;

  public subscriptions: Subscription[] = [];
  public currentRoute: string;
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public tableViewMode: string;
  public showBackToTop: boolean = false;
  public windowSizeLgUp: boolean;
  DEFAULT_DEBOUNCE_CHARACTERS = DEFAULT_DEBOUNCE_CHARACTERS;

  private pageIndex = 1;
  private maxIndex: number;

  constructor(
    private _globalService: GlobalService,
    private _router: Router,
    private _contractsAPIService: ContractAPIService,
    private _windowEventService: WindowEventsService,
    private _scrollService: ScrollService,
    private _decimalPipe: DecimalPipe,
    private _partnerAPIService: PartnerAPIService,
    private _ngbModal: NgbModal
  ) {
    this.currentRoute = this._router.url.split('/')[1];
  }

  ngOnInit() {
    this.tableViewMode = setTableViewMode(this._globalService, this.currentRoute, TABLE_VIEW_MODES.CARDS_VIEW);

    this.contractsFilter = {
      activeContracts: true,
      terminationUntilDate: null,
      contractTypeCodes: [],
      contractAssetIds: [],
      isPaid: undefined,
      isNotPaid: undefined,
      isOverdue: undefined,
      searchText: '',
    } as app.models.ContractsFilterModel;
    this.initialFilterState = structuredClone(this.contractsFilter);

    this.activeFilterCount = 0;

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(isBackToTopDisplayed => {
      this.showBackToTop = isBackToTopDisplayed;
    });
    this.subscriptions.push(backToTopSubscription);

    let scrollSubscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._contractsAPIService.getPagedAdminContracts(this.pageIndex, this.contractsFilter).subscribe(response => {
          this.contracts = this.contracts.concat(this.processContractsOverdue(response.data.values));
          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
    this.subscriptions.push(scrollSubscription);

    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    let subscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  loadPagedContracts(filter: app.models.ContractsFilterModel, forcedFocusSearchBar: boolean = false) {
    this.pageIndex = 1;
    this._contractsAPIService.getPagedAdminContracts(this.pageIndex, filter).subscribe(response => {
      this.contracts = this.processContractsOverdue(response.data.values);
      this.contractsProps = response.props;
      this.hasData = response.data.totalCount > 0;
      this.maxIndex = response.data.totalPages;

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;

      if (forcedFocusSearchBar) {
        this.contractSearchControl.focus();
      }
    });
  }

  processContractsOverdue(contracts: app.models.ContractModel[]) {
    return contracts.map(contract => ({
      ...contract,
      hasOverdue: contract.overdueAmounts.some(amount => amount.value > 0),
      overdueAmountsString: contract.overdueAmounts
        .map(amount => this._decimalPipe.transform(amount.value, '1.2-2') + ' ' + amount.code)
        .join(', '),
    }));
  }

  filterDataSource = debounce((value: string) => {
    this.partnerSearchValue = value;
    if (this.partnerSearchValue.length < 3) {
      this.isCompanySelected = false;
      this.contracts = [];
      this.hasData = this.contracts.some(x => x);
      delete this.contractsProps;
    }
    value.length >= DEFAULT_DEBOUNCE_CHARACTERS
      ? this.loadAutocompleteDateSource(value)
      : (this.autocompleteDatasource = []);
  });

  loadAutocompleteDateSource(input: string) {
    this._partnerAPIService.getPartners(input).subscribe(x => {
      this.autocompleteDatasource = x;
      this.hasPartners = this.autocompleteDatasource?.length > 0;
    });
  }

  partnerSelectedHandler(selectedPartnerTenant: any) {
    this.isCompanySelected = false;
    if (selectedPartnerTenant) {
      let partnerTenant = this.autocompleteDatasource.find(x => x.name == selectedPartnerTenant);
      if (partnerTenant) {
        this.autocompleteValue = { id: partnerTenant.partnerId, value: partnerTenant.name };
        this.contractsFilter.partnerExternalEntityId = partnerTenant.partnerId;
        this.isCompanySelected = isNotEmpty(selectedPartnerTenant);
        this.loadPagedContracts(this.contractsFilter);
      }
    } else {
      this.autocompleteValue = { id: undefined, value: '' };
    }
  }

  public filterHandler() {
    if (this.autocompleteValue.id == undefined) return;

    let modal = this._ngbModal.open(ContractListFilterComponent, {
      scrollable: true,
    });
    modal.componentInstance.contractsFilter = this.contractsFilter;
    modal.componentInstance.initialFilterState = this.initialFilterState;
    modal.componentInstance.partnerExternalEntityId = this.autocompleteValue?.id;
    modal.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = structuredClone(this.contractsFilter);
        this.activeFilterCount = result;
        this.loadPagedContracts(this.contractsFilter);
      }
    });
  }

  toggleContractListStateHandler() {
    this.contractsFilter.activeContracts = !this.contractsFilter.activeContracts;
    this.loadPagedContracts(this.contractsFilter);
  }

  resetFiltersHandler(fullFilterReset: boolean = false) {
    if (fullFilterReset) {
      this.autocompleteValue = { id: undefined, value: '' };
    }
    this.contractsFilter = {
      activeContracts: true,
      terminationUntilDate: null,
      contractTypeCodes: [],
      contractAssetIds: [],
      isPaid: undefined,
      isNotPaid: undefined,
      isOverdue: undefined,
      searchText: '',
      partnerExternalEntityId: this.autocompleteValue.id,
    } as app.models.ContractsFilterModel;
    this.initialFilterState = structuredClone(this.contractsFilter);

    this.activeFilterCount = 0;
    this.pageIndex = 1;
    this.loadPagedContracts(this.contractsFilter);
  }

  public toggleViewMode(tableViewMode: string) {
    this.tableViewMode = tableViewMode;
    this._globalService.toggleTableViewMode(this.currentRoute, tableViewMode);
  }

  scrollToTop(): void {
    scrollTop();
  }
}
